// Sound notification system for CRM
// Uses Web Audio API with user-interaction unlock

let audioContext = null;
let soundUnlocked = false;

function getAudioContext() {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
}

// Unlock audio on first user interaction
function unlockAudio() {
  if (soundUnlocked) return;
  try {
    const ctx = getAudioContext();
    if (ctx.state === "suspended") ctx.resume();
    // Play a silent buffer to unlock
    const buffer = ctx.createBuffer(1, 1, 22050);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);
    soundUnlocked = true;
  } catch {}
}

// Auto-unlock on any interaction
if (typeof document !== "undefined") {
  ["click", "touchstart", "keydown"].forEach(evt => {
    document.addEventListener(evt, unlockAudio, { once: false, passive: true });
  });
}

export function playNotificationSound(volume = 0.5) {
  if (volume === 0) return;
  try {
    const ctx = getAudioContext();
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;

    // Create a pleasant "ding-dong" notification sound
    // First tone (high)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(880, now); // A5
    gain1.gain.setValueAtTime(volume * 0.6, now);
    gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.15);

    // Second tone (higher)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(1318, now + 0.12); // E6
    gain2.gain.setValueAtTime(0, now);
    gain2.gain.setValueAtTime(volume * 0.7, now + 0.12);
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.12);
    osc2.stop(now + 0.4);

    // Third harmonic for richness
    const osc3 = ctx.createOscillator();
    const gain3 = ctx.createGain();
    osc3.type = "triangle";
    osc3.frequency.setValueAtTime(1760, now + 0.1); // A6
    gain3.gain.setValueAtTime(0, now);
    gain3.gain.setValueAtTime(volume * 0.3, now + 0.1);
    gain3.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
    osc3.connect(gain3);
    gain3.connect(ctx.destination);
    osc3.start(now + 0.1);
    osc3.stop(now + 0.5);
  } catch (e) {
    console.warn("Sound play failed:", e);
  }
}

export function playChatSound(volume = 0.5) {
  if (volume === 0) return;
  try {
    const ctx = getAudioContext();
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;

    // Quick "pop" sound for chat messages
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(600, now);
    osc.frequency.exponentialRampToValueAtTime(1200, now + 0.08);
    gain.gain.setValueAtTime(volume * 0.8, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.2);

    // Second pop
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(900, now + 0.1);
    osc2.frequency.exponentialRampToValueAtTime(1400, now + 0.18);
    gain2.gain.setValueAtTime(0, now);
    gain2.gain.setValueAtTime(volume * 0.6, now + 0.1);
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.35);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.1);
    osc2.stop(now + 0.35);
  } catch (e) {
    console.warn("Chat sound play failed:", e);
  }
}

export function playUrgentSound(volume = 0.5) {
  if (volume === 0) return;
  try {
    const ctx = getAudioContext();
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;

    // Urgent alarm — 3 rapid beeps
    for (let i = 0; i < 3; i++) {
      const t = now + i * 0.2;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      osc.frequency.setValueAtTime(1000, t);
      gain.gain.setValueAtTime(volume * 0.5, t);
      gain.gain.exponentialRampToValueAtTime(0.01, t + 0.12);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.12);
    }
  } catch (e) {
    console.warn("Urgent sound play failed:", e);
  }
}

// Get/set volume from localStorage
export function getSoundVolume() {
  const saved = localStorage.getItem("crm_sound_volume");
  return saved !== null ? parseFloat(saved) : 0.7;
}

export function setSoundVolume(val) {
  localStorage.setItem("crm_sound_volume", String(val));
}
